import * as React from 'react';
import styled, { css } from 'styled-components';
import { rem } from 'polished';
import { ButtonStyles } from '../../components/_helpers/form/Button';
import { __ } from '@bart.sk-eshop/react-i18n/lib';
import { CaretStyles } from '../../components/_helpers/Icon/Caret';
import OnClickOutsideComponent from '../../components/_helpers/OnClickOutsideComponent/OnClickOutsideComponent';
import Pagination from '../../components/Pagination/Pagination';
import { Media, MediaContextProvider } from '../../theme/media';

const Wrapper = styled(OnClickOutsideComponent)`
  margin-right: ${({ theme }) => rem(-theme.grid.padding / 2)};
  margin-left: ${({ theme }) => rem(-theme.grid.padding / 2)};
  border-bottom: ${rem(1)} solid #d6d6d6;
  display: flex;
  justify-content: space-between;

  ${({ theme }) => theme.media('sm')} {
    border-radius: ${({ theme }) => rem(theme.borderRadius.default)};
    margin-right: 0;
    margin-left: 0;
  }
`;

const WrapperSelect = styled(OnClickOutsideComponent)`
  background: ${({ theme }) => theme.color.white};
  margin-right: ${({ theme }) => rem(-theme.grid.padding / 2)};
  margin-left: ${({ theme }) => rem(-theme.grid.padding / 2)};
  ${({ theme }) => theme.media('sm')} {
    border-radius: ${({ theme }) => rem(theme.borderRadius.default)};
    margin-right: 0;
    margin-left: 0;
    box-shadow: ${({ theme }) => theme.boxShadow.small};
  }
`;

const ButtonWrapper = styled.div`
  position: relative;

  & + & {
    border-top: ${rem(2)} solid ${({ theme }) => theme.color.gray93};
  }

  ${({ theme }) => theme.media('sm')} {
    min-height: ${rem(46)};
    display: inline-block;

    & + & {
      border-top: none;
    }
  }
`;

const ToggleButton = styled.button`
  ${ButtonStyles};
  font-size: ${rem(14)};
  display: block;
  border-radius: ${({ theme }) => rem(theme.borderRadius.default)}
  width: 100%;
  text-align: left;
  padding: ${rem(17)} ${rem(35)} ${rem(17)} ${rem(20)};
  outline: none;
  ${({ theme }) => theme.media('sm')} {
    display: inline-block;
    width: auto;
  }

  position: absolute;
  top: ${rem(-45)};
  right: 0;

  ${({ theme }) => theme.media('sm')} {
    position: initial;
  }
`;

const Caret = styled.span`
  ${CaretStyles};
  position: absolute;
  top: 50%;
  right: ${rem(20)};
  transform: translateY(-50%);
`;

const DropdownStyles = css`
  background: ${({ theme }) => theme.color.white};
  width: 100%;
  ${({ theme }) => theme.media('sm')} {
    border-radius: ${({ theme }) => rem(theme.borderRadius.default)};
    width: ${rem(300)};
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 11;
    transform: translateY(${rem(2)});
    box-shadow: ${({ theme }) => theme.boxShadow.small};
  }
`;

const SortDropdown = styled.div`
  ${DropdownStyles};
  padding: ${rem(10)} 0;
`;

const TopPaginationWrap = styled.div`
  &.hide-compact {
    @media (min-width: 992px) and (max-width: 1450px) {
      display: none;
    }
  }
`;

const Sort = styled.button`
  ${ButtonStyles};
  width: 100%;
  font-size: ${rem(14)};
  padding: ${rem(10)} ${rem(20)};
  text-align: left;
  &.active {
    background: ${({ theme }) => theme.color.gray95};
  }
`;

const FilterTabWrapper = styled.div`
  display: flex;
  gap: ${rem(12)};
  height: 100%;
  margin-left: ${rem(2)};
  align-items: flex-end;
  margin-right: ${rem(15)};
`;

const FilterTab = styled.div`
  border: ${rem(1)} solid #d6d6d6;
  border-bottom: none;
  padding: ${rem(8)} ${rem(32)} ${rem(16)};
  font-size: ${rem(15)};
  font-weight: ${({ isActive }) => (isActive ? 600 : 400)};
  color: #171717;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-top-left-radius: ${rem(6)};
  border-top-right-radius: ${rem(6)};
  background: ${({ isActive }) =>
    isActive
      ? '#ffffff'
      : 'linear-gradient(180deg, rgba(0, 0, 0, 0) 70%, rgba(0, 0, 0, 0.05) 100%), #ebebeb'};
  min-height: ${rem(44)};
  text-align: center;
`;

interface Props {
  onSortChange: (sort: string, sortDir: string) => void;
  sort: Array<{
    name: string;
    sort: string;
    sortDir: string;
  }>;
  paginationTotal?: number;
  paginationCurrent?: number | string;
  onPageChanged?: (pageNumner: number) => void;
  isSearch: boolean;
  isSortable: boolean;
  sortCurrent?: string;
  sortDirCurrent?: string;
  hideMobileSort?: boolean;
  hideMobilePagination?: boolean;
  hideCompactPagination?: boolean;
}

interface States {
  visibleDropdown: number | null;
}

class Filter extends React.PureComponent<Props, States> {
  constructor(props) {
    super(props);

    this.state = {
      visibleDropdown: null,
    };
  }

  public setVisibleDropdownIndex = (index) => {
    if (
      (index !== null && this.state.visibleDropdown === null) ||
      index !== this.state.visibleDropdown
    ) {
      this.setState({
        visibleDropdown: index,
      });
    } else {
      this.setState({
        visibleDropdown: null,
      });
    }
  };

  public setSort = (sort, sortDir) => {
    this.props.onSortChange(sort, sortDir);
  };

  public getWrappedRenderTab() {
    const { sortCurrent, sortDirCurrent } = this.props;

    return (
      <ButtonWrapper>
        {this.props.isSortable ? (
          <FilterTabWrapper>
            {this.props.sort.map((sort, i) => {
              return (
                <FilterTab
                  isActive={
                    (sortCurrent === sort.sort &&
                      sortDirCurrent === sort.sortDir) ||
                    (!sortCurrent && i === 0)
                  }
                  onClick={this.setSort.bind(null, sort.sort, sort.sortDir)}
                >
                  {' '}
                  {sort.name}{' '}
                </FilterTab>
              );
            })}
          </FilterTabWrapper>
        ) : (
          <></>
        )}
      </ButtonWrapper>
    );
  }

  public getWrappedRenderSelect() {
    const { sortCurrent, sortDirCurrent } = this.props;

    return (
      <ButtonWrapper>
        {this.props.isSortable ? (
          <>
            <ToggleButton onClick={this.setVisibleDropdownIndex.bind(null, 2)}>
              {__('Zoradiť')} <Caret />
            </ToggleButton>
            {this.state.visibleDropdown === 2 && (
              <SortDropdown>
                {this.props.sort.map((sort, i) => {
                  return (
                    <Sort
                      key={i}
                      onClick={this.setSort.bind(null, sort.sort, sort.sortDir)}
                      className={
                        (sortCurrent === sort.sort &&
                          sortDirCurrent === sort.sortDir) ||
                        (this.props.isSearch &&
                          sort.sort === 'sale_count' &&
                          sortCurrent === '') ||
                        (!this.props.isSearch &&
                          sort.sort === 'sale_count' &&
                          sortCurrent === '')
                          ? 'active'
                          : ''
                      }
                    >
                      {sort.name}
                    </Sort>
                  );
                })}
              </SortDropdown>
            )}
          </>
        ) : (
          ''
        )}
      </ButtonWrapper>
    );
  }

  public render() {
    let { paginationCurrent } = this.props;
    const { paginationTotal, onPageChanged } = this.props;
    if (typeof paginationCurrent === 'string') {
      paginationCurrent = parseInt(paginationCurrent, 10);
    }
    return (
      <MediaContextProvider>
        <Media greaterThanOrEqual="md">
          <Wrapper
            onClickOutside={this.setVisibleDropdownIndex.bind(null, null)}
          >
            {this.getWrappedRenderTab()}

            {paginationCurrent && paginationTotal && onPageChanged ? (
              <TopPaginationWrap
                className={
                  this.props.hideCompactPagination ? 'hide-compact' : undefined
                }
              >
                <Pagination
                  current={paginationCurrent}
                  total={paginationTotal}
                  onPageChanged={onPageChanged}
                  inFilter={true}
                  filled={false}
                />
              </TopPaginationWrap>
            ) : (
              ''
            )}
          </Wrapper>
        </Media>

        <Media lessThan="md">
          {!this.props.hideMobileSort && (
            <WrapperSelect
              onClickOutside={this.setVisibleDropdownIndex.bind(null, null)}
            >
              {this.getWrappedRenderSelect()}
            </WrapperSelect>
          )}
          {!this.props.hideMobilePagination &&
          paginationCurrent &&
          paginationTotal &&
          onPageChanged ? (
            <Pagination
              current={paginationCurrent}
              total={paginationTotal}
              onPageChanged={onPageChanged}
              inFilter={false}
            />
          ) : (
            ''
          )}
        </Media>
      </MediaContextProvider>
    );
  }
}

export default Filter;
